import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, useApi, type ApiFailure } from '../lib/api.ts';
import { Empty, ErrorState, Icon, Loading, Sheet, Status, can, orgPath, useOrg, useToast } from '../lib/ui.tsx';
import type { Overlay } from '@lumacast/contracts';
import type { Asset } from './Library.tsx';
import { OverlaysPanel } from './Overlays.tsx';

interface Item { itemId?: string; assetId?: string | null; page?: number | null; streamId?: string | null; durationMs: number; fit: 'contain' | 'cover'; audio: 'mute' | 'duck' | 'exclusive' }
interface Playlist { id: string; name: string; items: Item[]; overlays?: Overlay[]; draftVersion: number; publishedRevision: number | null; hasUnpublishedChanges: boolean | null }

export function Playlists() {
  const org = useOrg();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const { data, error, loading, reload } = useApi<{ playlists: Playlist[] }>(orgPath(org, '/playlists'), [org.id]);
  const [creating, setCreating] = useState(params.get('new') === '1');
  const [name, setName] = useState('');
  const selected = data?.playlists.find((p) => p.id === params.get('id')) ?? data?.playlists[0];
  async function create() {
    try {
      const r = await api<{ id: string }>('POST', orgPath(org, '/playlists'), { name, items: [] });
      setCreating(false);
      setName('');
      await reload();
      setParams({ id: r.id });
    } catch (e) {
      toast((e as ApiFailure).message, 'danger');
    }
  }
  return (
    <>
      <div className="page-head">
        <div><h1>Playlists</h1><p>Edits are drafts. Publishing freezes an immutable revision; screens never change until you publish.</p></div>
        {can(org, 'playlists.write') && <button className="btn btn-primary" onClick={() => setCreating(true)}><Icon name="plus" /> New playlist</button>}
      </div>
      {error && <ErrorState error={error} retry={reload} />}
      {loading && !data && <Loading />}
      {data && !data.playlists.length && <div className="card"><Empty title="No playlists yet">A playlist is an ordered loop of media for a screen zone.</Empty></div>}
      {data && !!data.playlists.length && (
        <div className="grid" style={{ gridTemplateColumns: 'minmax(200px, 260px) 1fr', alignItems: 'start' }}>
          <nav className="card" aria-label="Playlists">
            {data.playlists.map((p) => (
              <button key={p.id} className="nav-link" style={{ width: '100%', border: 0, background: p.id === selected?.id ? 'var(--action-soft)' : 'transparent', cursor: 'pointer', font: 'inherit' }}
                aria-current={p.id === selected?.id ? 'page' : undefined} onClick={() => setParams({ id: p.id })}>
                <span style={{ flex: 1, textAlign: 'left' }}>{p.name}</span>
                {p.hasUnpublishedChanges && <span className="badge warn plain">draft</span>}
              </button>
            ))}
          </nav>
          {selected && <Editor key={selected.id} playlist={selected} onSaved={reload} />}
        </div>
      )}
      {creating && (
        <Sheet title="New playlist" onClose={() => setCreating(false)} footer={<button className="btn btn-primary" disabled={!name} onClick={() => void create()}>Create</button>}>
          <div className="field"><label htmlFor="pl-name">Name</label><input id="pl-name" className="input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && name && void create()} /></div>
        </Sheet>
      )}
    </>
  );
}

function Editor({ playlist, onSaved }: { playlist: Playlist; onSaved: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const assets = useApi<{ assets: Asset[] }>(orgPath(org, '/assets?status=ready'), [org.id]);
  const streams = useApi<{ streams: { id: string; name: string }[] }>(orgPath(org, '/streams'), [org.id]);
  const [items, setItems] = useState<Item[]>(playlist.items);
  const [overlays, setOverlays] = useState<Overlay[]>(playlist.overlays ?? []);
  const [version, setVersion] = useState(playlist.draftVersion);
  const [state, setState] = useState<'saved' | 'saving' | 'unsaved' | 'conflict'>('saved');
  const [picking, setPicking] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const editable = can(org, 'playlists.write');
  const byId = new Map(assets.data?.assets.map((a) => [a.id, a]));

  // Autosave with optimistic concurrency (draftVersion): never silently overwrite another editor.
  useEffect(() => {
    if (JSON.stringify(items) === JSON.stringify(playlist.items) && JSON.stringify(overlays) === JSON.stringify(playlist.overlays ?? [])) return;
    setState('unsaved');
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setState('saving');
      try {
        const r = await api<{ draftVersion: number }>('PUT', orgPath(org, `/playlists/${playlist.id}`), { items, overlays, draftVersion: version });
        setVersion(r.draftVersion);
        setState('saved');
        onSaved();
      } catch (e) {
        setState((e as ApiFailure).status === 409 ? 'conflict' : 'unsaved');
        toast((e as ApiFailure).message, 'danger');
      }
    }, 800);
    return () => clearTimeout(timer.current);
  }, [items, overlays]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (i: number, d: -1 | 1) => setItems((xs) => { const n = [...xs]; const [x] = n.splice(i, 1); n.splice(i + d, 0, x!); return n; });
  const update = (i: number, patch: Partial<Item>) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const total = items.reduce((n, x) => n + (byId.get(x.assetId ?? '')?.playback?.durationMs ?? x.durationMs), 0);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>{playlist.name}</h2>
          <span className="subtle">{items.length} items · loop {Math.round(total / 1000)}s · published revision {playlist.publishedRevision ?? 'none'}</span>
        </div>
        <div className="row">
          <span className="subtle" aria-live="polite">{state === 'saving' ? 'Saving…' : state === 'saved' ? 'All changes saved' : state === 'conflict' ? 'Changed elsewhere — reload' : 'Unsaved'}</span>
          {editable && <button className="btn" onClick={() => setPicking(true)}><Icon name="plus" /> Add media</button>}
        </div>
      </div>
      {!items.length ? <Empty title="This playlist is empty" action={editable ? <button className="btn btn-primary" onClick={() => setPicking(true)}>Add media</button> : undefined} /> : (
        <ol style={{ listStyle: 'none', margin: 0, padding: 8 }} aria-label="Playlist items">
          {items.map((it, i) => {
            const a = byId.get(it.assetId ?? '');
            const s = streams.data?.streams.find((x) => x.id === it.streamId);
            const isVideo = a?.kind === 'video' || !!it.streamId;
            return (
              <li key={i} className="row" style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
                <span className="mono subtle" style={{ width: 22 }}>{i + 1}</span>
                <div className="thumb" style={{ width: 96, flex: 'none' }}>{a?.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <Icon name={it.streamId ? 'play' : 'library'} />}</div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <strong>{a?.name ?? s?.name ?? 'Missing media'}</strong>
                  <div className="subtle">{a ? `${a.kind}${a.pageCount ? ` · page ${it.page ?? 1}` : ''}` : s ? 'live HLS stream · falls back to cached content offline' : ''}</div>
                </div>
                {!isVideo || it.streamId ? (
                  <label className="row subtle">Seconds <input className="input" style={{ width: 80 }} type="number" min={1} max={86400} value={it.durationMs / 1000} disabled={!editable} onChange={(e) => update(i, { durationMs: Math.max(1000, Number(e.target.value) * 1000) })} aria-label={`Duration of item ${i + 1}`} /></label>
                ) : <span className="subtle">{((a?.playback?.durationMs ?? 0) / 1000).toFixed(1)}s (video length)</span>}
                <select className="input" style={{ width: 'auto' }} value={it.fit} disabled={!editable} onChange={(e) => update(i, { fit: e.target.value as Item['fit'] })} aria-label={`Fit of item ${i + 1}`}>
                  <option value="contain">Fit</option><option value="cover">Fill</option>
                </select>
                {isVideo && (
                  <select className="input" style={{ width: 'auto' }} value={it.audio} disabled={!editable} onChange={(e) => update(i, { audio: e.target.value as Item['audio'] })} aria-label={`Audio policy of item ${i + 1}`}>
                    <option value="duck">Duck music</option><option value="exclusive">Pause music</option><option value="mute">Mute video</option>
                  </select>
                )}
                {editable && (
                  <span className="row" style={{ gap: 4 }}>
                    <button className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Move item ${i + 1} up`}>↑</button>
                    <button className="btn btn-ghost btn-sm" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={`Move item ${i + 1} down`}>↓</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} aria-label={`Remove item ${i + 1}`}><Icon name="close" size={14} /></button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <OverlaysPanel value={overlays} onChange={setOverlays} editable={editable} />
      {picking && (
        <Sheet title="Add media" subtitle="Only processed (ready) media can be added." onClose={() => setPicking(false)} wide>
          <div className="grid grid-cards">
            {assets.data?.assets.filter((a) => a.kind !== 'audio').map((a) => (
              <button key={a.id} className="card" style={{ padding: 0, textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                onClick={() => { setItems((xs) => [...xs, ...(a.kind === 'pdf' ? Array.from({ length: a.pageCount ?? 1 }, (_, p) => ({ assetId: a.id, page: p + 1, durationMs: 10000, fit: 'contain' as const, audio: 'duck' as const })) : [{ assetId: a.id, durationMs: 10000, fit: 'contain' as const, audio: 'duck' as const }])]); setPicking(false); }}>
                <div className="thumb" style={{ borderRadius: '16px 16px 0 0' }}>{a.thumbnailUrl ? <img src={a.thumbnailUrl} alt="" /> : <Icon name="library" />}</div>
                <div style={{ padding: 12 }}><strong>{a.name}</strong><div className="row" style={{ justifyContent: 'space-between' }}><span className="subtle">{a.kind}</span><Status value={a.status} /></div></div>
              </button>
            ))}
            {streams.data?.streams.map((s) => (
              <button key={s.id} className="card card-pad" style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
                onClick={() => { setItems((xs) => [...xs, { streamId: s.id, durationMs: 300_000, fit: 'contain', audio: 'duck' }]); setPicking(false); }}>
                <Icon name="play" /> <strong>{s.name}</strong><div className="subtle">Live HLS stream</div>
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}
